import type { CSSProperties, ReactElement, ReactNode, MouseEvent } from "react";
import { forwardRef } from "react";
import Chip from "@mui/material/Chip";
import { resolveTagColor } from "./tagColor";

// Drop-in replacement for antd <Tag> on MUI <Chip>. Presents antd's API
// (children as label, color-name/hex, closable/onClose) so the ~38 call sites
// swap by import only. Color mapping lives in tagColor.ts (unit-tested).

export interface TagProps {
  children?: ReactNode;
  color?: string;
  closable?: boolean;
  onClose?: (e: MouseEvent) => void;
  onClick?: (e: MouseEvent) => void;
  icon?: ReactElement;
  style?: CSSProperties;
  className?: string;
  id?: string;
  title?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

// forwardRef: MUI <Tooltip> anchors on its child's ref; without it a tooltip
// wrapped around a Tag never shows on hover.
const TagBase = forwardRef<HTMLDivElement, TagProps>(function Tag({
  children,
  color,
  closable,
  onClose,
  onClick,
  icon,
  style,
  className,
  ...rest
}, ref) {
  // Many call sites still color tags via the legacy antd className `ant-tag--<hue>`
  // (e.g. ant-tag--blue/green) instead of the `color` prop; that CSS is gone with
  // antd, so those tags render faint. Derive the color from the className as a shim.
  const classHue = className?.match(
    /ant-tag--(blue|green|red|volcano|orange|gold|yellow|lime|cyan|geekblue|purple|magenta|success|error|warning|processing)\b/,
  )?.[1];
  const { color: chipColor, variant, sx } = resolveTagColor(color ?? classHue);
  return (
    <Chip
      ref={ref}
      label={children}
      size="small"
      color={chipColor}
      variant={variant}
      onClick={onClick}
      icon={icon}
      onDelete={closable ? (onClose as (e: MouseEvent) => void) : undefined}
      style={style}
      className={className}
      // antd <Tag> carried a default margin-inline-end that layouts relied on for
      // spacing; MUI Chip has none, so tags rendered flush against each other.
      sx={{ marginInlineEnd: 0.5, marginBottom: 0.5, ...sx }}
      {...rest}
    />
  );
});

// antd <Tag.CheckableTag>: a toggleable tag driven by `checked`/`onChange(next)`.
export interface CheckableTagProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  id?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

const CheckableTag = ({ checked, onChange, children, style, className, ...rest }: CheckableTagProps) => (
  <Chip
    label={children}
    size="small"
    color={checked ? "primary" : "default"}
    variant={checked ? "filled" : "outlined"}
    onClick={() => onChange?.(!checked)}
    style={style}
    className={className}
    {...rest}
  />
);

export const Tag = Object.assign(TagBase, { CheckableTag });

export default Tag;
